import { useState, useEffect, useMemo } from "react";
import { useSpring, useTransition, animated } from "@react-spring/web";
import {
  X,
  MousePointerClick,
  ArrowUpRight,
  ArrowDownLeft,
  ChevronDown,
  Database,
  Info,
  Layers,
  GitFork,


} from "lucide-react";
import { useOrg360NodeDetail } from "@/hooks/org360/useOrg360NodeDetail";
import { useOrg360Impact } from "@/hooks/org360/useOrg360Impact";
import type { Org360IncludeFlags } from "@/hooks/org360/useOrg360GraphData";
import { nodeColor } from "./org360Constants";
import { cn } from "@/lib/utils";
import type {
  Org360Node,
  Org360RelationItem,
  Org360Field,
  BlastRadiusItem,
} from "./org360Types";

// ── Interfaces ────────────────────────────────────────────────────────────────
interface OrgInspectorPanelProps {
  open: boolean;
  onClose: () => void;
  selectedNode: Org360Node | null;
  onNavigate?: (node: { id: string; label: string; type: string }) => void;
  onHoverNode?: (id: string | null) => void;
  graphNodeIds?: Set<string>;
  graphHoveredNodeId?: string | null;
  includeFlags?: Org360IncludeFlags;
}

type PanelTab = "details" | "blast-radius";
type SectionId = "about" | "references" | "referenced-by" | "fields";
type BlastDir = "dependents" | "dependencies";

const TABS: { id: PanelTab; label: string; icon: React.ReactNode }[] = [
  { id: "details",      label: "Details",      icon: <Info    className="h-3 w-3" /> },
  { id: "blast-radius", label: "Blast Radius",  icon: <GitFork className="h-3 w-3" /> },
];

// ── Type badge ────────────────────────────────────────────────────────────────
function TypeBadge({ type }: { type: string }) {
  const color = nodeColor(type);
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold"
      style={{ background: `${color}22`, color }}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
      {type}
    </span>
  );
}

// ── Stat pill ─────────────────────────────────────────────────────────────────
function StatPill({ icon, value, label }: { icon: React.ReactNode; value: number | string; label: string }) {
  return (
    <div className="flex items-center gap-1 px-2 py-1 rounded-md border border-border bg-muted/40">
      <span className="text-muted-foreground flex-shrink-0">{icon}</span>
      <span className="font-mono text-[11px] font-bold leading-none">{value}</span>
      <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-wide leading-none">{label}</span>
    </div>
  );
}

// ── Collapsible section ───────────────────────────────────────────────────────
function CollapsibleSection({
  title, icon, count, isOpen, onToggle, maxContentHeight = 320, children,
}: {
  title: string; icon: React.ReactNode; count?: number; isOpen: boolean;
  onToggle: () => void; maxContentHeight?: number; children: React.ReactNode;
}) {
  const contentSpring = useSpring({
    maxHeight: isOpen ? maxContentHeight : 0,
    opacity:   isOpen ? 1 : 0,
    config: { tension: 320, friction: 28 },
  });
  const chevronSpring = useSpring({
    rotate: isOpen ? 0 : -90,
    config: { tension: 420, friction: 24 },
  });

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-muted/40 transition-colors duration-150 text-left"
      >
        <animated.span
          className="inline-flex flex-shrink-0"
          style={{ transform: chevronSpring.rotate.to((r) => `rotate(${r}deg)`) }}
        >
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </animated.span>
        <span className="text-muted-foreground flex-shrink-0">{icon}</span>
        <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-foreground flex-1">{title}</span>
        {count !== undefined && (
          <span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground flex-shrink-0">
            {count}
          </span>
        )}
      </button>
      <animated.div style={{ maxHeight: contentSpring.maxHeight, opacity: contentSpring.opacity, overflow: "hidden" }}>
        <div className="overflow-y-auto" style={{ maxHeight: maxContentHeight }}>
          {children}
        </div>
      </animated.div>
    </div>
  );
}

// ── Description with show-more ────────────────────────────────────────────────
function DescriptionBlock({ text, role }: { text: string; role?: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > 180;
  const expandSpring = useSpring({ maxHeight: expanded || !isLong ? 1000 : 68, config: { tension: 300, friction: 28 } });

  return (
    <div className="px-4 pb-3 flex flex-col items-start gap-2">
      <div className="relative w-full">
        <animated.div style={{ maxHeight: expandSpring.maxHeight, overflow: "hidden" }}>
          <p className="text-[12px] text-muted-foreground leading-relaxed">{text}</p>
        </animated.div>
        {isLong && !expanded && (
          <div className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none"
            style={{ background: "linear-gradient(to top, hsl(var(--card) / 0.95), transparent)" }} />
        )}
      </div>
      {(isLong || role) && (
        <div className="flex items-center justify-between w-full">
          {isLong ? (
            <button type="button" onClick={() => setExpanded((v) => !v)}
              className="text-[11px] font-mono text-primary hover:underline">
              {expanded ? "Show less ↑" : "Show more ↓"}
            </button>
          ) : <span />}
          {role && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
              {role}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Relation row ──────────────────────────────────────────────────────────────
function RelationRow({
  item, onNavigate, onHover, inGraph = true, isGraphHovered = false,
}: {
  item: Org360RelationItem;
  onNavigate?: (node: { id: string; label: string; type: string }) => void;
  onHover?: (id: string | null) => void;
  inGraph?: boolean;
  isGraphHovered?: boolean;
}) {
  const color = nodeColor(item.type);
  return (
    <button
      type="button"
      data-relation-id={item.id}
      onClick={() => onNavigate?.(item)}
      onMouseEnter={() => inGraph && onHover?.(item.id)}
      onMouseLeave={() => onHover?.(null)}
      className="w-full flex items-center gap-2 px-4 py-1.5 text-left hover:bg-muted/60 transition-colors duration-150 group"
      style={isGraphHovered ? { background: `${color}18`, borderLeft: `2px solid ${color}` } : { borderLeft: "2px solid transparent" }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-0.5"
        title={inGraph ? undefined : "Not in current graph view"}
        style={inGraph ? { background: color } : { border: `1.5px solid ${color}99`, background: `${color}22` }}
      />
      <div className="flex-1 min-w-0">
        <span className="block text-[12px] font-mono truncate text-foreground group-hover:text-primary transition-colors duration-100">{item.label}</span>
        <span className="block text-[10px] font-mono text-muted-foreground truncate">{item.type}</span>
      </div>
      {item.is_managed && (
        <span
          className="text-[9px] font-mono flex-shrink-0 px-1.5 py-0.5 rounded border border-dashed border-muted-foreground/50 text-muted-foreground"
          title="Managed-package component — shown because it connects to this node"
        >
          pkg
        </span>
      )}
      <span className="text-[10px] font-mono flex-shrink-0 px-1.5 py-0.5 rounded"
        style={{ background: `${color}18`, color: `${color}cc` }}>
        {item.rel}
      </span>
    </button>
  );
}

// ── Field row ─────────────────────────────────────────────────────────────────
function FieldRow({ field }: { field: Org360Field }) {
  return (
    <div className="flex items-center justify-between gap-2 px-4 py-1.5 hover:bg-muted/40 transition-colors duration-100">
      <span className="text-[12px] font-mono truncate text-foreground">{field.full_name}</span>
      {field.field_type && (
        <span className="text-[10px] font-mono text-muted-foreground flex-shrink-0 px-1.5 py-0.5 rounded bg-muted border border-border/60">
          {field.field_type}
        </span>
      )}
    </div>
  );
}

// ── Skeleton rows ─────────────────────────────────────────────────────────────
function SkeletonRows({ rows = 3 }: { rows?: number }) {
  return (
    <div className="px-4 py-2 space-y-2.5">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-muted animate-pulse flex-shrink-0" />
          <div className="h-3 rounded bg-muted animate-pulse" style={{ width: `${52 + (i % 3) * 14}%` }} />
          <div className="h-3 w-10 rounded bg-muted animate-pulse ml-auto flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}

// ── Blast radius row ──────────────────────────────────────────────────────────
function BlastRow({
  item, onNavigate,
}: {
  item: BlastRadiusItem;
  onNavigate?: (node: { id: string; label: string; type: string }) => void;
}) {
  const color = nodeColor(item.type);
  return (
    <button
      type="button"
      onClick={() => onNavigate?.(item)}
      className="w-full flex items-center gap-2 px-4 py-1.5 text-left hover:bg-muted/60 transition-colors duration-150 group"
    >
      <span
        className="font-mono text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded flex-shrink-0"
        style={{ color: `${color}99`, background: `${color}14` }}
      >
        {item.depth}
      </span>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
      <div className="flex-1 min-w-0">
        <span className="block text-[12px] font-mono truncate text-foreground group-hover:text-primary transition-colors duration-100">
          {item.label}
        </span>
        <span className="block text-[10px] font-mono text-muted-foreground truncate">{item.type}</span>
      </div>
      <span className="text-[9px] font-mono flex-shrink-0 px-1.5 py-0.5 rounded"
        style={{ background: `${color}18`, color: `${color}cc` }}>
        {item.rel}
      </span>
    </button>
  );
}

// ── Blast radius tab ──────────────────────────────────────────────────────────
function BlastRadiusTab({
  selectedNode, includeFlags, onNavigate,
}: {
  selectedNode: Org360Node;
  includeFlags?: Org360IncludeFlags;
  onNavigate?: (node: { id: string; label: string; type: string }) => void;
}) {
  const [direction, setDirection] = useState<BlastDir>("dependents");
  const [maxDepth, setMaxDepth] = useState(3);

  useEffect(() => {
    setDirection("dependents");
    setMaxDepth(3);
  }, [selectedNode.id]);

  // Blast radius from the Neo4j-backed impact engine (not the pruned graph) so
  // it's complete even for nodes that aren't in the default graph, and honours
  // the same field/report/managed toggles. dependents → incoming, deps → out.
  const { data: impact, isLoading } = useOrg360Impact(
    selectedNode.id,
    direction === "dependents" ? "in" : "out",
    maxDepth,
    includeFlags,
  );
  const items = useMemo(() => impact?.items ?? [], [impact]);

  const maxDepthFound = impact?.maxDepth ?? 0;
  const isTruncated = !!impact?.truncated;

  const relCounts = useMemo(
    () => Object.entries(impact?.byRel ?? {}).sort((a, b) => b[1] - a[1]).slice(0, 4),
    [impact],
  );

  // Animate the list out/in when direction or depth changes
  const listTransition = useTransition(`${direction}-${maxDepth}`, {
    from:  { opacity: 0, y: 6 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: -4, position: "absolute" as const, inset: 0 },
    config: { tension: 360, friction: 28 },
    exitBeforeEnter: true,
  });

  // Pill toggle: numeric spring so .to() interpolation works correctly
  const dirIndex = direction === "dependents" ? 0 : 1;
  const pillSpring = useSpring({
    x: dirIndex * 50,   // 0 or 50 (percent)
    config: { tension: 400, friction: 30 },
  });

  return (
    <div className="flex flex-col h-full">

      {/* ── Row 1: direction pill (full width) ── */}
      <div className="px-3 pt-3 pb-2 flex-shrink-0">
        <div className="relative flex p-0.5 rounded-lg bg-muted">
          <animated.span
            className="absolute top-0.5 bottom-0.5 w-1/2 rounded-md bg-card shadow-sm"
            style={{ left: pillSpring.x.to((x) => `${x}%`) }}
            aria-hidden
          />
          {(["dependents", "dependencies"] as BlastDir[]).map((dir) => (
            <button
              key={dir}
              type="button"
              onClick={() => setDirection(dir)}
              className={cn(
                "relative z-10 flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-md",
                "text-[11px] font-mono font-semibold transition-colors duration-150",
                direction === dir ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {dir === "dependents"
                ? <ArrowDownLeft className="h-3 w-3" />
                : <ArrowUpRight  className="h-3 w-3" />}
              {dir === "dependents" ? "Dependents" : "Dependencies"}
            </button>
          ))}
        </div>
      </div>

      {/* ── Row 2: depth label + chips ── */}
      <div className="px-3 pb-2 flex items-center gap-2 flex-shrink-0">
        <span className="font-mono text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
          Depth
        </span>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setMaxDepth(d)}
              className={cn(
                "w-7 h-6 rounded text-[11px] font-mono font-bold transition-colors duration-100",
                maxDepth === d
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground",
              )}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* ── Stats: inline text, no divider ── */}
      {items.length > 0 && (
        <div className="px-4 pb-2 flex items-center flex-wrap gap-x-2 gap-y-1 flex-shrink-0">
          <span className="font-mono text-[11px] font-bold text-foreground">
            {items.length}{isTruncated ? "+" : ""}
          </span>
          <span className="font-mono text-[10px] text-muted-foreground">
            {direction === "dependents" ? "impacted" : "dependencies"}
            {maxDepthFound > 0 && ` · depth ${maxDepthFound}${isTruncated ? "+" : ""}`}
          </span>
          <span className="w-full" />
          {relCounts.map(([rel, count]) => (
            <span key={rel} className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              {rel.toLowerCase()} {count}
            </span>
          ))}
        </div>
      )}

      {/* ── List — single top border ── */}
      <div className="flex-1 relative overflow-hidden border-t border-border">
        {isLoading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-[12px] text-muted-foreground font-mono">Computing blast radius…</p>
          </div>
        ) : listTransition((style, _key) =>
          items.length === 0 ? (
            <animated.div
              style={style}
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center"
            >
              <GitFork className="h-6 w-6 text-muted-foreground/30" />
              <p className="text-[12px] text-muted-foreground font-mono">
                {direction === "dependents"
                  ? "Nothing depends on this node."
                  : "This node has no outgoing dependencies."}
              </p>
            </animated.div>
          ) : (
            <animated.div style={style} className="absolute inset-0 overflow-y-auto">
              <div className="pb-2">
                {items.map((item) => (
                  <BlastRow key={`${item.id}-${item.depth}`} item={item} onNavigate={onNavigate} />
                ))}
                {isTruncated && (
                  <p className="px-4 py-2 text-[10px] font-mono text-muted-foreground text-center border-t border-border">
                    More beyond this depth — reduce depth or open a node to keep exploring
                  </p>
                )}
              </div>
            </animated.div>
          )
        )}
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export function OrgInspectorPanel({
  open,
  onClose,
  selectedNode,
  onNavigate,
  onHoverNode,
  graphNodeIds,
  graphHoveredNodeId,
  includeFlags,
}: OrgInspectorPanelProps) {
  const { data: detail, isLoading } = useOrg360NodeDetail(selectedNode?.id ?? null);

  const [activeTab, setActiveTab] = useState<PanelTab>("details");
  const [activeSection, setActiveSection] = useState<SectionId | null>("references");

  const panelSpring = useSpring({
    transform: open ? "translateX(0%)" : "translateX(105%)",
    opacity: open ? 1 : 0,
    config: { tension: 300, friction: 28 },
  });

  useEffect(() => {
    setActiveTab("details");
    setActiveSection("references");
  }, [selectedNode?.id]);

  // If the active tab is no longer available (e.g., switched from object → non-object node), reset
  useEffect(() => {
    if (!TABS.find((t) => t.id === activeTab)) setActiveTab("details");
  }, [TABS, activeTab]);

  const toggle = (id: SectionId) =>
    setActiveSection((prev) => (prev === id ? null : id));

  useEffect(() => {
    if (!graphHoveredNodeId) return;
    const el = document.querySelector<HTMLElement>(`[data-relation-id="${graphHoveredNodeId}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [graphHoveredNodeId]);

  // Sliding tab indicator.
  // The span width = 1/N of the container. translateX(100%) moves it exactly
  // one tab-width to the right — so step = 100% per tab index, not 100/N.
  const tabIndex = TABS.findIndex((t) => t.id === activeTab);
  const indicatorSpring = useSpring({
    x: tabIndex * 100,   // 0 → under tab 0, 100 → under tab 1
    config: { tension: 400, friction: 30 },
  });

  // Tab content cross-fade transition
  const tabTransition = useTransition(activeTab, {
    from:  { opacity: 0, y: 8 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: -6 },
    config: { tension: 340, friction: 26 },
    exitBeforeEnter: true,
  });

  return (
    <animated.aside
      className="absolute inset-y-0 right-0 z-30 w-80 max-w-[calc(100%-2rem)] flex flex-col
        bg-card/95 backdrop-blur-md border-l border-border overflow-hidden"
      style={panelSpring}
      aria-hidden={!open}
    >
      {/* Panel header */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-border flex-shrink-0">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close inspector panel"
          className="grid place-items-center w-6 h-6 rounded-full flex-shrink-0
            text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Inspector
        </span>
      </div>

      {/* Empty state */}
      {!selectedNode && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center py-8 px-6">
          <div className="grid place-items-center w-10 h-10 rounded-full bg-muted text-muted-foreground">
            <MousePointerClick className="h-5 w-5" />
          </div>
          <p className="text-[13px] text-muted-foreground leading-relaxed">
            Click any node in the graph to inspect its fields, references, and blast radius.
          </p>
        </div>
      )}

      {/* Node content */}
      {selectedNode && (
        <div className="flex flex-col flex-1 min-h-0">

          {/* Identity block */}
          <div className="px-4 pt-4 pb-4 border-b border-border flex-shrink-0">
            <TypeBadge type={selectedNode.type} />
            <h3 className="mt-2 font-display font-bold text-[15px] leading-snug break-words">
              {selectedNode.label}
            </h3>
            {selectedNode.full_name && selectedNode.full_name !== selectedNode.label && (
              <p className="mt-0.5 text-[10px] font-mono text-muted-foreground truncate" title={selectedNode.full_name}>
                {selectedNode.full_name}
              </p>
            )}
            {selectedNode.file_path && (
              <p className="mt-0.5 text-[10px] font-mono text-muted-foreground/60 truncate" title={selectedNode.file_path}>
                {selectedNode.file_path}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-1.5">
              <StatPill icon={<ArrowDownLeft className="h-3 w-3" />} value={isLoading ? "…" : (detail?.incoming?.length ?? 0)} label="used by" />
              <StatPill icon={<ArrowUpRight  className="h-3 w-3" />} value={isLoading ? "…" : (detail?.outgoing?.length  ?? 0)} label="uses" />
              {!isLoading && (detail?.fields?.length ?? 0) > 0 && (
                <StatPill icon={<Layers className="h-3 w-3" />} value={detail!.fields.length} label="fields" />
              )}
            </div>
          </div>

          {/* Tab bar — sliding underline */}
          <div className="relative flex border-b border-border flex-shrink-0">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 py-2.5",
                  "text-[11px] font-mono font-semibold transition-colors duration-150",
                  activeTab === tab.id ? "text-primary" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
            {/* Animated sliding underline */}
            <animated.span
              className="absolute bottom-0 h-[2px] bg-primary rounded-t-full pointer-events-none"
              style={{
                width: `${100 / TABS.length}%`,
                transform: indicatorSpring.x.to((x) => `translateX(${x}%)`),
              }}
            />
          </div>

          {/* Tab content — cross-fade transition */}
          <div className="relative flex-1 min-h-0 overflow-hidden">
            {tabTransition((style, tab) => (
              <animated.div
                style={{ ...style, position: "absolute", inset: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
              >
                {/* ── Details tab ── */}
                {tab === "details" && (
                  <div className="flex-1 overflow-y-auto min-h-0">

                    {(isLoading || detail?.summary || detail?.role) && (
                      <CollapsibleSection
                        title="About" icon={<Info className="h-3.5 w-3.5" />}
                        isOpen={activeSection === "about"} onToggle={() => toggle("about")} maxContentHeight={200}
                      >
                        {isLoading ? (
                          <div className="px-4 py-2 space-y-2">
                            <div className="h-3 rounded bg-muted animate-pulse w-full" />
                            <div className="h-3 rounded bg-muted animate-pulse w-4/5" />
                            <div className="h-3 rounded bg-muted animate-pulse w-3/5" />
                          </div>
                        ) : detail?.summary ? (
                          <DescriptionBlock text={detail.summary} role={detail.role} />
                        ) : (
                          <div className="px-4 pb-3">
                            <span className="inline-block text-[10px] font-mono px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                              {detail!.role}
                            </span>
                          </div>
                        )}
                      </CollapsibleSection>
                    )}

                    <CollapsibleSection
                      title="References" icon={<ArrowUpRight className="h-3.5 w-3.5" />}
                      count={isLoading ? undefined : (detail?.outgoing?.length ?? 0)}
                      isOpen={activeSection === "references"} onToggle={() => toggle("references")} maxContentHeight={320}
                    >
                      {isLoading ? <SkeletonRows rows={3} /> : (detail?.outgoing?.length ?? 0) === 0 ? (
                        <p className="px-4 py-3 text-[12px] text-muted-foreground font-mono italic">No outgoing references.</p>
                      ) : (
                        <div className="pb-1">
                          {detail!.outgoing.map((item) => (
                            <RelationRow key={`${item.id}-${item.rel}`} item={item} onNavigate={onNavigate} onHover={onHoverNode}
                              inGraph={!graphNodeIds || graphNodeIds.has(item.id)} isGraphHovered={graphHoveredNodeId === item.id} />
                          ))}
                        </div>
                      )}
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Referenced By" icon={<ArrowDownLeft className="h-3.5 w-3.5" />}
                      count={isLoading ? undefined : (detail?.incoming?.length ?? 0)}
                      isOpen={activeSection === "referenced-by"} onToggle={() => toggle("referenced-by")} maxContentHeight={320}
                    >
                      {isLoading ? <SkeletonRows rows={3} /> : (detail?.incoming?.length ?? 0) === 0 ? (
                        <p className="px-4 py-3 text-[12px] text-muted-foreground font-mono italic">Nothing references this.</p>
                      ) : (
                        <div className="pb-1">
                          {detail!.incoming.map((item) => (
                            <RelationRow key={`${item.id}-${item.rel}`} item={item} onNavigate={onNavigate} onHover={onHoverNode}
                              inGraph={!graphNodeIds || graphNodeIds.has(item.id)} isGraphHovered={graphHoveredNodeId === item.id} />
                          ))}
                        </div>
                      )}
                    </CollapsibleSection>

                    {(isLoading || (detail?.fields?.length ?? 0) > 0) && (
                      <CollapsibleSection
                        title="Fields" icon={<Database className="h-3.5 w-3.5" />}
                        count={isLoading ? undefined : detail?.fields?.length}
                        isOpen={activeSection === "fields"} onToggle={() => toggle("fields")} maxContentHeight={320}
                      >
                        {isLoading ? <SkeletonRows rows={5} /> : (
                          <div className="pb-1">
                            {detail!.fields.map((f) => <FieldRow key={f.full_name} field={f} />)}
                          </div>
                        )}
                      </CollapsibleSection>
                    )}

                  </div>
                )}

                {/* ── Blast Radius tab ── */}
                {tab === "blast-radius" && (
                  <BlastRadiusTab
                    selectedNode={selectedNode}
                    includeFlags={includeFlags}
                    onNavigate={onNavigate}
                  />
                )}

              </animated.div>
            ))}
          </div>

        </div>
      )}
    </animated.aside>
  );
}
